'use client'

/**
 * batch-68 handtekening (FO-345 §3.2) - de inhoud van het ondertekenvenster,
 * gedeeld door de portal (bottom-sheet, z-[60]) en de backoffice (dialoog).
 *
 *  1. Bovenaan het document met "Document lezen": tekenen kan pas nadat het
 *     document is geopend, of na het vinkje "Ik heb het document gelezen".
 *  2. Handtekening: de opgeslagen handtekening staat voorgevuld met "Anders
 *     tekenen" (+ "Ook opslaan als mijn handtekening"); zonder opgeslagen
 *     handtekening een leeg tekenvak met "Opslaan als mijn handtekening" (aan).
 *     Nooit automatisch: elke ondertekening is een bewuste klik.
 *  2b. Plaats (alleen als het vak een plaats-stippellijn heeft, bv. een
 *     formulier van de Belastingdienst): voorgevuld met de woonplaats, verplicht.
 *  3. De verklaring (bij een gespreksverslag de FO-332-tekst).
 *  4. Verhoogde bevestiging: eerst opnieuw inloggen (de app regelt de login).
 *  5. "Ondertekenen" -> spinner -> een voorbeeld van het getekende document
 *     -> "Klaar".
 *
 * Offline: tekenen kan alleen met verbinding; er is geen stille wachtrij.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { SignaturePad, type SignaturePadHandle } from '../signature-pad'
import { cn } from '../../lib/utils'

export type OndertekenKeuze = 'OPGESLAGEN' | { dataUrl: string; aantalStreken: number }

export type OndertekenUitkomst =
  | { ok: true; resultaatTekst: string; bekijkGetekend?: ReactNode }
  | { ok: false; melding: string }

/** De aanroep zelf viel om (verbinding weg, server-action onbereikbaar). */
const VERBINDING_MISLUKT =
  'De verbinding viel weg. Kijk of je handtekening is gezet (ververs de pagina) en probeer het anders opnieuw.'

export interface OndertekenPaneelProps {
  titel: string
  verklaring: string
  bevestiging: 'STANDAARD' | 'VERHOOGD'
  /**
   * Mijn vak dat aan de beurt is; null = niets te tekenen. `plaats` gezet = het
   * vak vraagt de plaats van ondertekening (verplicht), met een voorstel.
   */
  mijnBeurt: { rolLabel: string; plaats?: { voorstel: string | null } | null } | null
  /** "Eerst moet X tekenen." als ik nog moet wachten. */
  wachtOp: string | null
  statusTekst: string
  /** Opgeslagen handtekening (voorbeeld via de eigenaar-URL). */
  opgeslagen: { voorbeeldUrl: string } | null
  /** De knop/viewer om het document te lezen; roep `onGeopend` aan zodra het open is. */
  documentLezen: (opties: { onGeopend: () => void }) => ReactNode
  /** Verhoogde bevestiging: KLAAR = er is een geldig herauth-token. */
  herauth: { status: 'NIET_NODIG' | 'NODIG' | 'BEZIG' | 'KLAAR'; melding?: string | null; onStart: () => void }
  onTekenen: (input: {
    handtekening: OndertekenKeuze
    opslaanAlsStandaard: boolean
    /** Alleen gezet als het vak om een plaats vraagt. */
    plaats?: string
  }) => Promise<OndertekenUitkomst>
  onKlaar: () => void
  onAnnuleren?: () => void
  /** De gedeelde kwaliteitscheck (`beoordeelHandtekeningKwaliteit` uit @streep/types). */
  beoordeel: (input: { soort: 'HANDTEKENING'; aantalStreken: number; breedtePx: number }) => string | null
  /** De offline-melding (`ONDERTEKENEN_OFFLINE_MELDING`). */
  offlineMelding: string
  /** Extra acties onder de knop (bv. "Reageren of bezwaar maken" bij een verslag). */
  extraActies?: ReactNode
  className?: string
}

function useOnline(): boolean {
  const [online, setOnline] = useState(true)
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    setOnline(navigator.onLine !== false)
    const aan = () => setOnline(true)
    const uit = () => setOnline(false)
    window.addEventListener('online', aan)
    window.addEventListener('offline', uit)
    return () => {
      window.removeEventListener('online', aan)
      window.removeEventListener('offline', uit)
    }
  }, [])
  return online
}

export function OndertekenPaneel(props: OndertekenPaneelProps) {
  const padRef = useRef<SignaturePadHandle>(null)
  const online = useOnline()
  const [geopend, setGeopend] = useState(false)
  const [gelezenVinkje, setGelezenVinkje] = useState(false)
  const [andersTekenen, setAndersTekenen] = useState(props.opgeslagen == null)
  const [opslaan, setOpslaan] = useState(props.opgeslagen == null)
  const [padLeeg, setPadLeeg] = useState(true)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const [klaar, setKlaar] = useState<{ tekst: string; bekijk?: ReactNode } | null>(null)
  const vraagtPlaats = props.mijnBeurt?.plaats != null
  const [plaats, setPlaats] = useState(props.mijnBeurt?.plaats?.voorstel ?? '')
  const plaatsOk = !vraagtPlaats || plaats.trim().length > 0

  const gelezen = geopend || gelezenVinkje
  const metOpgeslagen = props.opgeslagen != null && !andersTekenen
  const herauthOk = props.herauth.status === 'NIET_NODIG' || props.herauth.status === 'KLAAR'
  const kanTekenen =
    props.mijnBeurt != null && gelezen && online && herauthOk && plaatsOk && !bezig && (metOpgeslagen || !padLeeg)

  async function teken() {
    setFout(null)
    let keuze: OndertekenKeuze
    if (metOpgeslagen) {
      keuze = 'OPGESLAGEN'
    } else {
      const pad = padRef.current
      if (!pad) return
      const aantalStreken = pad.aantalStreken?.() ?? (pad.isEmpty() ? 0 : 2)
      const melding = props.beoordeel({ soort: 'HANDTEKENING', aantalStreken, breedtePx: pad.inktBreedte?.() ?? 0 })
      if (melding) {
        setFout(melding)
        return
      }
      keuze = { dataUrl: pad.toDataURL(), aantalStreken }
    }
    if (!online) {
      setFout(props.offlineMelding)
      return
    }
    setBezig(true)
    let res: OndertekenUitkomst | undefined
    try {
      res = await props.onTekenen({
        handtekening: keuze,
        opslaanAlsStandaard: !metOpgeslagen && opslaan,
        ...(vraagtPlaats ? { plaats: plaats.trim() } : {}),
      })
    } catch {
      // batch-68 (review I7) - een weggevallen verbinding mag het paneel nooit
      // op "bezig" laten hangen.
      setFout(VERBINDING_MISLUKT)
      return
    } finally {
      setBezig(false)
    }
    // Een doorverwijzing (bv. opnieuw inloggen) geeft geen uitkomst: niets doen.
    if (!res) return
    if (!res.ok) {
      setFout(res.melding)
      return
    }
    setKlaar({ tekst: res.resultaatTekst, bekijk: res.bekijkGetekend })
  }

  if (klaar) {
    return (
      <div className={cn('space-y-4', props.className)}>
        <div className="rounded-md border border-streep-border bg-streep-surface-alt p-3">
          <p className="text-sm font-semibold text-streep-donker">&#10003; {klaar.tekst}</p>
          <p className="mt-1 text-xs text-streep-text-muted">
            Je handtekening staat in het vak van het document. De ongetekende versie blijft bewaard.
          </p>
        </div>
        {klaar.bekijk}
        <button
          type="button"
          onClick={props.onKlaar}
          className="min-h-11 w-full rounded-md bg-streep-donker px-3 text-sm font-semibold text-white"
        >
          Klaar
        </button>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', props.className)}>
      {/* 1. Document */}
      <div className="space-y-2">
        <p className="line-clamp-2 text-sm font-medium text-streep-donker">{props.titel}</p>
        {props.documentLezen({ onGeopend: () => setGeopend(true) })}
        {!geopend && (
          <label className="flex min-h-11 items-center gap-3 text-sm text-streep-donker">
            <input
              type="checkbox"
              checked={gelezenVinkje}
              onChange={(e) => setGelezenVinkje(e.target.checked)}
              className="h-5 w-5"
            />
            Ik heb het document gelezen
          </label>
        )}
      </div>

      {props.wachtOp && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">{props.wachtOp}</p>
      )}

      {!props.mijnBeurt && !props.wachtOp && (
        <p className="rounded-md bg-streep-surface-alt p-3 text-sm text-streep-text-muted">
          Er is voor jou niets (meer) te tekenen. Status: {props.statusTekst}
        </p>
      )}

      {props.mijnBeurt && (
        <>
          {/* 2. Handtekening */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-streep-donker">
              Tekenen als {props.mijnBeurt.rolLabel.toLowerCase()}
            </p>
            {metOpgeslagen && props.opgeslagen ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center rounded-md border border-streep-border bg-white p-3">
                  {/* eslint-disable-next-line @next/next/no-img-element -- kortlevende eigenaar-URL via de proxy */}
                  <img
                    src={props.opgeslagen.voorbeeldUrl}
                    alt="Je opgeslagen handtekening"
                    className="max-h-24 max-w-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-streep-text-muted">Je opgeslagen handtekening</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAndersTekenen(true)
                      setOpslaan(false)
                    }}
                    className="min-h-11 px-2 text-sm font-medium text-streep-donker underline underline-offset-2"
                  >
                    Anders tekenen
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <SignaturePad
                  ref={padRef}
                  height={170}
                  basislijn
                  label="Teken hier je handtekening"
                  toonVoetbalk={false}
                  disabled={bezig}
                  onChange={(leeg) => {
                    setPadLeeg(leeg)
                    if (!leeg) setFout(null)
                  }}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={bezig || padLeeg}
                    onClick={() => padRef.current?.clear()}
                    className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm text-streep-donker disabled:opacity-50"
                  >
                    Wissen
                  </button>
                  <button
                    type="button"
                    disabled={bezig || padLeeg}
                    onClick={() => padRef.current?.undo?.()}
                    className="min-h-11 flex-1 rounded-md border border-streep-border bg-white px-3 text-sm text-streep-donker disabled:opacity-50"
                  >
                    Ongedaan maken
                  </button>
                </div>
                <label className="flex min-h-11 items-center gap-3 text-sm text-streep-donker">
                  <input
                    type="checkbox"
                    checked={opslaan}
                    onChange={(e) => setOpslaan(e.target.checked)}
                    className="h-5 w-5"
                  />
                  {props.opgeslagen ? 'Ook opslaan als mijn handtekening' : 'Opslaan als mijn handtekening'}
                </label>
                {props.opgeslagen && (
                  <button
                    type="button"
                    onClick={() => setAndersTekenen(false)}
                    className="text-xs text-streep-text-muted underline-offset-2 hover:underline"
                  >
                    Toch mijn opgeslagen handtekening gebruiken
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 2b. Plaats (extern formulier met een plaats-stippellijn) */}
          {vraagtPlaats && (
            <div className="space-y-1">
              <label htmlFor="onderteken-plaats" className="text-sm font-medium text-streep-donker">
                Plaats
              </label>
              <input
                id="onderteken-plaats"
                type="text"
                value={plaats}
                maxLength={80}
                autoComplete="address-level2"
                disabled={bezig}
                onChange={(e) => setPlaats(e.target.value)}
                aria-invalid={!plaatsOk}
                aria-describedby="onderteken-plaats-uitleg"
                className="min-h-11 w-full rounded-md border border-streep-border bg-white px-3 text-base text-streep-donker md:text-sm"
              />
              <p id="onderteken-plaats-uitleg" className="text-xs text-streep-text-muted">
                {plaatsOk
                  ? 'Komt samen met de datum van vandaag op het document te staan.'
                  : 'Vul de plaats in waar je tekent.'}
              </p>
            </div>
          )}

          {/* 3. Verklaring */}
          <p className="text-xs text-streep-text-muted">{props.verklaring}</p>

          {/* 4. Verhoogde bevestiging */}
          {props.bevestiging === 'VERHOOGD' && props.herauth.status !== 'KLAAR' && (
            <div className="space-y-2 rounded-md border border-streep-border bg-streep-surface-alt p-3">
              <p className="text-sm text-streep-donker">
                Voor dit document bevestig je eerst je identiteit: log opnieuw in met je
                Microsoft-account. Daarna kom je hier terug en teken je.
              </p>
              {props.herauth.melding && <p className="text-sm text-red-700">{props.herauth.melding}</p>}
              <button
                type="button"
                disabled={props.herauth.status === 'BEZIG' || !online}
                onClick={props.herauth.onStart}
                className="min-h-11 w-full rounded-md border border-streep-donker bg-white px-3 text-sm font-semibold text-streep-donker disabled:opacity-50"
              >
                {props.herauth.status === 'BEZIG' ? 'Bezig met bevestigen...' : 'Opnieuw inloggen'}
              </button>
            </div>
          )}
          {props.bevestiging === 'VERHOOGD' && props.herauth.status === 'KLAAR' && (
            <p className="text-xs text-streep-text-muted">Je identiteit is bevestigd. Je kunt nu tekenen.</p>
          )}

          {!online && (
            <p role="status" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {props.offlineMelding}
            </p>
          )}

          {!gelezen && (
            <p className="text-xs text-streep-text-muted">
              Open eerst het document, of vink aan dat je het hebt gelezen.
            </p>
          )}

          {/* 5. Ondertekenen */}
          <button
            type="button"
            disabled={!kanTekenen}
            onClick={teken}
            className="min-h-11 w-full rounded-md bg-streep-donker px-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {bezig ? 'Bezig met ondertekenen...' : 'Ondertekenen'}
          </button>
        </>
      )}

      {fout && (
        <p role="alert" className="text-sm text-red-700">
          {fout}
        </p>
      )}
      {props.extraActies}
      {props.onAnnuleren && (
        <button
          type="button"
          onClick={props.onAnnuleren}
          disabled={bezig}
          className="min-h-11 w-full rounded-md border border-streep-border bg-white px-3 text-sm font-medium text-streep-donker disabled:opacity-50"
        >
          Annuleren
        </button>
      )}
    </div>
  )
}
